import { RISING_IN_SIGN, type RisingEntry } from '../data/risingInSign';
import { SUN_IN_SIGN, type SunEntry } from '../data/sunInSign';
import { MOON_IN_SIGN, type MoonEntry } from '../data/moonInSign';
import { SUN_RISING_COMBOS, type ComboEntry } from '../data/sunRisingCombos';
import { PLANET_IN_SIGN, type PlanetSignEntry } from '../data/planetInSign';
import { PLANET_IN_HOUSE, type PlanetHouseEntry } from '../data/planetInHouse';
import { ASPECT_LIBRARY, type AspectEntry } from '../data/aspectLibrary';

/**
 * Centralized accessors for the Phase 3 content libraries.
 * All return `null` on miss so callers can safely fall back.
 */

export function getRising(sign: string | null | undefined): RisingEntry | null {
  if (!sign) return null;
  return (RISING_IN_SIGN as Record<string, RisingEntry>)[sign] ?? null;
}

export function getSunInSign(sign: string | null | undefined): SunEntry | null {
  if (!sign) return null;
  return (SUN_IN_SIGN as Record<string, SunEntry>)[sign] ?? null;
}

export function getMoonInSign(sign: string | null | undefined): MoonEntry | null {
  if (!sign) return null;
  return (MOON_IN_SIGN as Record<string, MoonEntry>)[sign] ?? null;
}

export function getSunRising(
  sun: string | null | undefined,
  rising: string | null | undefined,
): ComboEntry | null {
  if (!sun || !rising) return null;
  const key = `${sun}-${rising}`;
  return (SUN_RISING_COMBOS as Record<string, ComboEntry>)[key] ?? null;
}

/**
 * Phase 4 accessors — planet-in-sign / planet-in-house / aspect library.
 * Sun, Moon, and Rising remain in their dedicated lookups (Phase 3); these
 * cover Mercury → Node placements and the inter-planet aspect grid.
 */

export function getPlanetInSign(
  planet: string | null | undefined,
  sign: string | null | undefined,
): PlanetSignEntry | null {
  if (!planet || !sign) return null;
  return (PLANET_IN_SIGN as Record<string, PlanetSignEntry>)[`${planet}-${sign}`] ?? null;
}

export function getPlanetInHouse(
  planet: string | null | undefined,
  house: number | null | undefined,
): PlanetHouseEntry | null {
  if (!planet || !house) return null;
  return (PLANET_IN_HOUSE as Record<string, PlanetHouseEntry>)[`${planet}-${house}`] ?? null;
}

/**
 * Aspect lookup is order-insensitive in the planet pair — the underlying
 * library keys planets in the canonical order used at generation time, so we
 * try both orderings before giving up. Aliases for ASC/MC ("Rising", "Asc")
 * are normalized to skip lookup since the library only covers planet pairs.
 */
const ASPECT_PLANET_SET = new Set([
  'Sun','Moon','Mercury','Venus','Mars','Jupiter','Saturn','Uranus','Neptune','Pluto','Chiron',
]);

export function getAspect(
  p1: string | null | undefined,
  aspect: string | null | undefined,
  p2: string | null | undefined,
): AspectEntry | null {
  if (!p1 || !p2 || !aspect) return null;
  if (!ASPECT_PLANET_SET.has(p1) || !ASPECT_PLANET_SET.has(p2)) return null;
  const lib = ASPECT_LIBRARY as Record<string, AspectEntry>;
  return lib[`${p1}-${aspect}-${p2}`] ?? lib[`${p2}-${aspect}-${p1}`] ?? null;
}

export type {
  RisingEntry,
  SunEntry,
  MoonEntry,
  ComboEntry,
  PlanetSignEntry,
  PlanetHouseEntry,
  AspectEntry,
};